// Phase C.5e.1 — SCHEDULE CONSOLIDATION. This is THE one canonical
// schedule data-loading algorithm — no React, no react-router-dom, no
// component state. Pure async orchestration over the existing service
// layer, callable identically from BOTH the Team-focused route
// (/my-teams/:teamId/schedule) and the Division route
// (/my-programs/:programId/divisions/:divisionId/schedule), and directly
// unit-testable via jest.mock() on the service modules — no RTL, no
// react-router-dom resolution needed at all (see scheduleController.test.js).
//
// BROWSER-FOUND BUG THIS FILE FIXES — before this phase, TeamSchedulePage.js
// and DivisionSchedulePage.js each had their OWN, independently-evolved
// data-loading logic. They drifted: Team-focused mode had already been
// fixed (twice) to correctly resolve full Division membership and to
// never let a sibling's own external Games leak into Division standings,
// but DivisionSchedulePage.js's OWN, separate loader still had neither
// fix — so a plain Team Head Coach clicking "View Full Division" landed
// on a DIFFERENT, unfixed implementation and saw incomplete data again.
// Sharing <DivisionHub> as presentation was never enough on its own —
// the loader itself has to be the same one algorithm. This file (plus
// SchedulePage.js, which calls it) is that one algorithm.
//
// TWO MODES, ONE ALGORITHM:
//   - TEAM-FOCUSED MODE — `teamId` given. Program/Division are resolved
//     FROM the Team (`teams.program_id`/`division_id`, both direct
//     columns — no extra fetch). The focused Team's own events/matchups
//     always come from the private listTeamEvents()/listTeamMatchups()
//     (its own coach is always authorized for its own Team's full
//     private detail, regardless of Program authority).
//   - DIVISION MODE — `programId`+`divisionId` given, no `teamId`. There
//     is no focused Team; every Division Team is treated uniformly.
//
// Both modes resolve DIVISION MEMBERSHIP via the exact same
// resolveDivisionMembers() (divisionSchedule.js) — never
// list_division_sibling_teams(), whose real behavior for a plain Team
// Head Coach proved unreliable in the browser (see that function's own
// header for the full history):
//   - Program-authorized viewer -> listProgramSeasonTeams() (the same
//     raw, RLS-authorized Program roster read the ORIGINAL, already-
//     correct DivisionSchedulePage.js used), filtered to this Division.
//   - Everyone else -> listPublicDivisionTeams() (038) — the same
//     public-safe roster PublicDivisionPage.js already uses. No new RPC,
//     no broadened RLS, no private sibling Practice/Other/Notes.
// A bare per-Team entry is seeded for every member BEFORE the real
// schedule-data enrichment below runs, so a Team with zero games, zero
// relationship to the focused Team, or a failed/partial enrichment fetch
// still appears in the TEAMS panel — never contingent on enrichment
// succeeding.
//
// AUTHORITY is resolved ONCE, the same way in both modes:
// hasProgramManagementAuthority(program_id) — a Program Owner/Admin over
// the resolved Program. (has_program_management_authority_for_team(),
// migration 025, is defined as LITERALLY this same check resolved via a
// team_id instead of a bare program_id — mathematically identical for
// any given Team/Program pair — so there is exactly one authority
// question asked, regardless of mode.) Team-focused mode ALSO folds in
// team-level staff roles (team_admin/head_coach) for the focused Team
// specifically, since a plain Team Head Coach can always manage their
// OWN Team's plain events without needing Program authority at all —
// Division mode has no single "own Team," so its `canManage` is
// Program authority alone, exactly preserving DivisionSchedulePage.js's
// own original, intentional (not a bug) authority model: a plain Head
// Coach sees no Add Event / management controls in Division mode, only
// Program Owner/Admin does.

import { getTeam, listTeamStaff, listMyTeams } from '../../services/teamService';
import { getDivision, hasProgramManagementAuthority, listProgramSeasonTeams } from '../../services/programService';
import { listPublicDivisionGames, listPublicTeamGames, listPublicDivisionTeams } from '../../services/publicProgramService';
import { listTeamEvents, listTeamMatchups } from '../../services/scheduleService';
import { buildPublicPerTeam, resolveDivisionMembers } from '../../utils/divisionSchedule';

/**
 * @param {{ teamId?: string, programId?: string, divisionId?: string, currentUserId?: string }} params
 *   Pass `teamId` for TEAM-FOCUSED mode. Pass `programId`+`divisionId`
 *   (no `teamId`) for DIVISION mode.
 * @returns {{
 *   team: object|null, division: object|null, perTeam: array,
 *   canManage: boolean, canManageRow: (function|null),
 *   hasProgramAuthority: boolean, hasTeamAuthority: boolean, myRoles: string[],
 * }}
 */
export async function loadScheduleData({ teamId, programId, divisionId, currentUserId } = {}) {
  let team = null;
  let ownEntry = null;
  let myRoles = [];
  let resolvedProgramId = programId || null;
  let resolvedDivisionId = divisionId || null;

  if (teamId) {
    const [t, staff, teamEvents, teamMatchups] = await Promise.all([
      getTeam(teamId), listTeamStaff(teamId), listTeamEvents(teamId), listTeamMatchups(teamId),
    ]);
    team = t;
    resolvedProgramId = t.program_id;
    resolvedDivisionId = t.division_id;
    myRoles = staff.filter(s => s.user_id === currentUserId && s.status === 'active').map(s => s.role);
    ownEntry = { team: { id: t.id, team_name: t.team_name }, events: teamEvents, matchups: teamMatchups };
  }

  const hasProgramAuthority = resolvedProgramId
    ? await hasProgramManagementAuthority(resolvedProgramId).catch(() => false)
    : false;
  // Team-focused mode: a plain Team-level Head Coach/Admin manages their
  // OWN Team regardless of Program authority. Division mode: no single
  // "own Team" exists, so this collapses to Program authority alone —
  // exactly DivisionSchedulePage.js's original, intentional model.
  const hasTeamAuthority = teamId
    ? Boolean(hasProgramAuthority || myRoles.includes('team_admin') || myRoles.includes('head_coach'))
    : hasProgramAuthority;

  let division = null;
  let perTeam = ownEntry ? [ownEntry] : [];

  if (resolvedDivisionId) {
    // MEMBERSHIP — its own try/catch: a failure here degrades to "just
    // this Team, no Division-wide context" (Team-focused mode) or "no
    // Division Teams resolved" (Division mode) — never a wrong/partial
    // roster silently passed downstream.
    let members = [];
    try {
      if (hasProgramAuthority && resolvedProgramId) {
        const allProgramTeams = await listProgramSeasonTeams(resolvedProgramId);
        members = resolveDivisionMembers({ excludeTeamId: teamId, divisionId: resolvedDivisionId, teamAuthority: true, allProgramTeams });
      } else {
        const publicDivisionTeams = await listPublicDivisionTeams(resolvedDivisionId).catch(() => []);
        members = resolveDivisionMembers({ excludeTeamId: teamId, divisionId: resolvedDivisionId, teamAuthority: false, publicDivisionTeams });
      }
    } catch {
      members = [];
    }

    // Seed the guaranteed baseline BEFORE any schedule-data enrichment
    // below — a Division Team with zero games, zero relationship to the
    // focused Team (if any), or a failed/partial enrichment fetch still
    // appears here (with an empty "—" record), exactly like the TEAMS
    // panel requires. The enrichment step below only ever REPLACES this
    // baseline with richer data; it must never be the thing membership
    // depends on.
    perTeam = [...(ownEntry ? [ownEntry] : []), ...members.map(m => ({ team: m, events: [], matchups: [] }))];

    try {
      division = await getDivision(resolvedDivisionId);

      if (hasProgramAuthority) {
        // Same resilient private per-member fetch the original,
        // already-correct DivisionSchedulePage.js used — a Program-
        // authorized viewer can see every Division Team's own private
        // schedule, in either mode.
        const memberEntries = await Promise.all(members.map(async (m) => {
          const [e, ma] = await Promise.all([
            listTeamEvents(m.id).catch(() => []),
            listTeamMatchups(m.id).catch(() => []),
          ]);
          return { team: m, events: e, matchups: ma };
        }));
        perTeam = [...(ownEntry ? [ownEntry] : []), ...memberEntries];
      } else if (members.length > 0) {
        // Public-safe fallback — no private sibling access required, no
        // new RPC, never exposes Notes/Practice/Other. THIS branch is
        // exactly what the original DivisionSchedulePage.js was missing
        // entirely, which is why "View Full Division" produced
        // incomplete data for a plain Head Coach even after Team-focused
        // mode was fixed.
        //
        // ONE EXCEPTION, mirroring Team-focused mode's own established
        // rule ("this Team's own coach is always authorized for its own
        // Team's full private detail, regardless of Program authority")
        // — DIVISION MODE has no single focused Team, but the viewer may
        // still personally hold active staff on ONE OR MORE Division
        // Teams. listMyTeams() is an EXISTING, already-RLS-scoped read
        // (`.eq('user_id', userId)` — never broadened here) already used
        // elsewhere in the app; teams it returns with at least one active
        // staff role get the same private treatment their own Team-
        // focused Schedule already gives them (their own external Games,
        // Notes, Practice/Other) — every OTHER Division Team still only
        // ever gets public-safe data. Guardian-only entries (roles: [])
        // are excluded — a guardian is never team_staff and would be
        // rejected by list_team_events()/list_team_matchups() outright.
        const myTeams = currentUserId ? await listMyTeams(currentUserId).catch(() => []) : [];
        const myStaffTeamIds = new Set(myTeams.filter(t => (t.roles || []).length > 0).map(t => t.id));
        const myMembers = members.filter(m => myStaffTeamIds.has(m.id));
        const otherMembers = members.filter(m => !myStaffTeamIds.has(m.id));

        const [myMemberEntries, divisionGames, externalGamesEntries] = await Promise.all([
          Promise.all(myMembers.map(async (m) => {
            const [e, ma] = await Promise.all([
              listTeamEvents(m.id).catch(() => []),
              listTeamMatchups(m.id).catch(() => []),
            ]);
            return { team: m, events: e, matchups: ma };
          })),
          listPublicDivisionGames(resolvedDivisionId).catch(() => []),
          Promise.all(otherMembers.map(async (m) => [m.id, await listPublicTeamGames(m.id).catch(() => [])])),
        ]);
        const externalGamesByTeamId = Object.fromEntries(externalGamesEntries);
        const otherEntries = buildPublicPerTeam(otherMembers, divisionGames, externalGamesByTeamId);
        perTeam = [...(ownEntry ? [ownEntry] : []), ...myMemberEntries, ...otherEntries];
      }

      // BROWSER-FOUND BUG FIX (carried over) — a specific-Teams event on
      // the focused Team's own Schedule may reference a Division Team ID
      // that membership resolution above still didn't surface (e.g., the
      // public-safe path returning empty because the Program isn't
      // published). DivisionHub resolves participant NAMES purely from
      // `perTeam` — so a still-unresolved participant is added here as a
      // bare, schedule-empty entry purely so its name becomes resolvable.
      if (ownEntry) {
        const knownIds = new Set(perTeam.map(e => e.team.id));
        const missingIds = [...new Set(
          ownEntry.events.filter(e => e.scope_type === 'specific_teams').flatMap(e => e.scope_team_ids || [])
        )].filter(id => !knownIds.has(id));
        if (missingIds.length > 0) {
          const resolved = await Promise.all(missingIds.map(id => getTeam(id).catch(() => null)));
          const extra = resolved.filter(Boolean).map(rt => ({ team: { id: rt.id, team_name: rt.team_name }, events: [], matchups: [] }));
          perTeam = [...perTeam, ...extra];
        }
      }
    } catch {
      // Division metadata (name/season) / schedule-data enrichment is
      // best-effort — the focused Team's own Schedule (already resolved
      // above, Team-focused mode) must never be blocked by it.
      // Membership itself is unaffected: `perTeam` already holds the
      // full-roster baseline seeded above, outside this try block, and
      // this catch never resets it.
    }
  }

  const canManage = hasTeamAuthority;
  // Team-focused mode: an inherited entire-Division/specific-Teams event
  // affects Teams beyond the focused one, so managing IT specifically
  // requires actual Program-level authority — a Team-level Head Coach's
  // ordinary `canManage` is not enough merely because the event also
  // appears on their own Schedule. Division mode: `canManage` is already
  // Program-authority-only (see above), so every visible row is already
  // uniformly manageable or not — no per-row override needed, exactly
  // preserving DivisionSchedulePage.js's original flat-`canManage` model.
  const canManageRow = teamId
    ? (row) => (row.kind !== 'event' || !row.scope_type || row.scope_type === 'team' ? canManage : hasProgramAuthority)
    : null;

  return { team, division, perTeam, canManage, canManageRow, hasProgramAuthority, hasTeamAuthority, myRoles };
}
